/**
 * G4 — ce que la console doit dériver du contrat pour montrer une figure.
 *
 * Fonctions PURES, extraites de l'écran pour être testables sans moteur de
 * rendu. Elles ne calculent JAMAIS un palier de savoir : pas de moyenne, pas
 * de minimum ordinal, pas de « niveau global ». Il n'existe, dans aucun type
 * de ce fichier, un champ où un agrégat pourrait être écrit.
 */
import type {
  DimensionSavoir,
  NiveauMaitrise,
  ObservationSavoirDto,
  ProgressionSavoirDto,
} from '@silex/shared';

/** Les paliers les plus fragiles d'abord : c'est l'ordre de préparation d'une séance. */
const ORDRE_PALIER: Readonly<Record<NiveauMaitrise, number>> = {
  non_evalue: 0,
  en_decouverte: 1,
  en_consolidation: 2,
  en_voie_de_maitrise: 3,
  maitrise: 4,
};

/**
 * Un savoir vu par ses DIMENSIONS. `GET /v1/suivi/:id/progression` renvoie une
 * ligne par (savoir, dimension) : deux pour un savoir visuel, une seule pour un
 * savoir d'algèbre. On les rassemble pour l'AFFICHAGE — et pour rien d'autre :
 * ce type ne porte aucun champ où écrire un palier de savoir.
 */
export interface SavoirDeuxLectures {
  readonly savoirId: string;
  readonly code: string;
  readonly enonce: string;
  readonly porteLaLectureDeFigure: boolean;
  readonly paliers: Partial<Readonly<Record<DimensionSavoir, NiveauMaitrise>>>;
  /**
   * Total des observations du savoir. Une somme de FAITS DATÉS — jamais une
   * agrégation de niveaux (§5.7). Elle ne se rend jamais seule : voir
   * `nbObservationsParDimension` et `nommerObservations` (lot 1.3, BD-02).
   */
  readonly nbObservations: number;
  /** La décomposition du total, sans laquelle il ne peut pas être rapproché. */
  readonly nbObservationsParDimension: Partial<Readonly<Record<DimensionSavoir, number>>>;
  readonly derniereObservation: string | null;
}

/**
 * Regroupe les lignes par savoir en gardant les deux dimensions SÉPARÉES.
 *
 * Sans ce regroupement, un savoir visuel apparaîtrait DEUX FOIS dans la liste,
 * avec le même énoncé et deux paliers différents — le tuteur lirait un bug là
 * où le contrat dit une vérité. Et l'inverse serait pire : les fondre en une
 * ligne demanderait de choisir un palier, c'est-à-dire d'agréger.
 */
export function regrouperParSavoir(lignes: readonly ProgressionSavoirDto[]): SavoirDeuxLectures[] {
  const parSavoir = new Map<string, SavoirDeuxLectures>();
  for (const l of lignes) {
    const deja = parSavoir.get(l.savoirId);
    const derniere = [deja?.derniereObservation, l.derniereObservation]
      .filter((d): d is string => typeof d === 'string')
      .sort()
      .at(-1);
    parSavoir.set(l.savoirId, {
      savoirId: l.savoirId,
      code: l.code,
      enonce: l.enonce,
      porteLaLectureDeFigure: l.porteLaLectureDeFigure,
      paliers: { ...(deja?.paliers ?? {}), [l.dimension]: l.niveauMaitrise },
      nbObservations: (deja?.nbObservations ?? 0) + l.nbObservations,
      nbObservationsParDimension: {
        ...(deja?.nbObservationsParDimension ?? {}),
        [l.dimension]: l.nbObservations,
      },
      derniereObservation: derniere ?? null,
    });
  }
  return [...parSavoir.values()];
}

/**
 * Les savoirs dont la LECTURE DE FIGURE a été observée — ceux, et ceux-là
 * seuls, dont la console a des figures à montrer. Les plus fragiles d'abord :
 * c'est l'ordre de préparation d'une séance.
 */
export function savoirsAFigures(savoirs: readonly SavoirDeuxLectures[]): SavoirDeuxLectures[] {
  return savoirs
    .filter((s) => s.porteLaLectureDeFigure && s.paliers.lecture_figure !== undefined)
    .sort(
      (a, b) => ORDRE_PALIER[a.paliers.lecture_figure!] - ORDRE_PALIER[b.paliers.lecture_figure!],
    );
}

/**
 * « figure de biais » ou « orientation habituelle » — lu sur `rotationDeg` de
 * la spécification, jamais deviné.
 *
 * C'est le point pédagogique de tout l'écran, et il n'est porté par aucun
 * décompte : une réussite sur une figure en orientation habituelle — celle où
 * l'hypoténuse est le côté incliné, donc celle où la règle apprise et l'allure
 * du dessin donnent la même réponse — ne distingue pas les deux. Sans
 * l'orientation au journal, ce jugement est impossible.
 */
export function orientationDeLaFigure(observation: ObservationSavoirDto): string | null {
  const spec = observation.figureSpec;
  if (!spec) return null;
  const rotation = (((spec.rotationDeg ?? 0) % 360) + 360) % 360;
  return rotation === 0 ? 'Orientation habituelle' : 'Figure de biais';
}
